/**
 * User Repository Implementation
 * Implements the UserRepository interface
 * Coordinates between API calls and mappers
 */

import { UserRepository } from "../../domain/repositories/UserRepository";
import { UserEntity } from "../../domain/entities/UserEntity";
import { CompanyEntity } from "../../domain/entities/CompanyEntity";
import {
  createUser,
  listUsers,
  getUser as getUserApi,
  updateUser,
  deleteUser as deleteUserApi,
  assignUserToCompanies,
} from "../api/userApi";
import { UserMapper } from "../mappers/UserMapper";

export class UserRepositoryImpl implements UserRepository {
  async getUsers(
    page: number = 1,
    limit: number = 50,
    search?: string
  ): Promise<{
    users: UserEntity[];
    pagination: {
      current_page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  }> {
    const response = await listUsers(page, limit, search);
    return UserMapper.toListUsersResult(response);
  }

  async getUserById(userId: number): Promise<{
    user: UserEntity;
    assigned_companies: Array<{
      id: number;
      company_name: string;
      app_url: string;
      role_name: string | null;
      is_company_admin: boolean;
    }>;
    available_companies: CompanyEntity[];
  }> {
    const response = await getUserApi(userId);
    return {
      user: UserMapper.toUserEntity(response.data.user),
      assigned_companies: response.data.assigned_companies.map((c: any) => ({
        id: c.id,
        company_name: c.company_name,
        app_url: c.app_url,
        role_name: c.role_name || null,
        is_company_admin: c.is_company_admin || false,
      })),
      available_companies: response.data.available_companies.map((c: any) => ({
        id: c.id,
        company_name: c.company_name,
        app_url: c.app_url,
        is_active: true,
        msme: false,
        company_gst_enabled: false,
      })),
    };
  }

  async createUser(data: {
    email: string;
    password: string;
    userName?: string;
    company_ids?: number[];
    roles?: { [companyId: number]: string };
  }): Promise<{
    user: UserEntity;
    assigned_companies: CompanyEntity[];
  }> {
    const response = await createUser(data);
    return UserMapper.toCreateUserResult(response);
  }

  async updateUser(
    userId: number,
    data: Partial<UserEntity> & {
      assigned_company_ids?: number[];
      unassigned_company_ids?: number[];
      roles?: { [companyId: number]: string };
    }
  ): Promise<UserEntity> {
    // Convert entity to DTO format (boolean to number)
    const dtoData: any = { ...data };
    if (dtoData.is_active !== undefined) {
      dtoData.is_active = dtoData.is_active ? 1 : 0;
    }
    if (dtoData.is_master_admin !== undefined) {
      dtoData.is_master_admin = dtoData.is_master_admin ? 1 : 0;
    }
    const response = await updateUser(userId, dtoData);
    return UserMapper.toUserEntity(response.data.user);
  }

  async deleteUser(userId: number): Promise<void> {
    await deleteUserApi(userId);
  }

  async assignUserToCompanies(
    userId: number,
    companyIds: number[],
    roles?: { [companyId: number]: string }
  ): Promise<void> {
    await assignUserToCompanies(userId, companyIds, roles);
  }
}

